import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { ButtonBase, Typography } from '@mui/material';
import { ChatIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PopTipCDS from 'app/shared/componentsRestyled/coreDesignSystem/PopTipCDS';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import { requestEntityChatFilterAction } from 'store/teamChat/actions';

import useEntityReferenceIndex from '../hooks/useEntityReferenceIndex';
import { ENTITY_REFERENCE_TYPE } from '../TeamChat.entityReferences.utils';

import { pillCountStyles, pillStyles } from './TeamChatReferenceBadge.styles';
import { TeamChatReferenceBadgeTestIds } from './TeamChatReferenceBadge.testIds';
import {
  getReferenceBadgeAriaLabel,
  getReferenceBadgeTooltip,
} from './TeamChatReferenceBadge.utils';

// Renders NOTHING when the entity has never been referenced in chat — no layout shift on the
// financial-info rows / borrower cards this sits on (DEV-5448). Clicking (or Enter/Space —
// ButtonBase gives that for free) opens the loan chat drawer on the Internal channel filtered to
// this entity, reusing the SAME queued-Redux-intent mechanism "Discuss in chat" uses to reach
// across into the drawer's subtree from here (requestEntityChatFilterAction /
// pendingEntityChatFilter, consumed by LoanEditorChatDrawerInner + TeamChatChannel). This stays
// client-side filtering until the server-side `?subjectRefPath=` filter (DEV-5447) lands.
const TeamChatReferenceBadge = memo(({ chatId = null, entityType, entityId }) => {
  const dispatch = useDispatch();
  const index = useEntityReferenceIndex(chatId);
  const bucket = index[entityType]?.[entityId] ?? null;
  const count = bucket?.count ?? 0;

  const onClick = useCallback(() => {
    dispatch(
      requestEntityChatFilterAction({ chatId, entityType, entityId, label: bucket?.label ?? '' }),
    );
  }, [dispatch, chatId, entityType, entityId, bucket?.label]);

  if (!count) {
    return null;
  }

  return (
    <PopTipCDS arrow title={getReferenceBadgeTooltip(count)}>
      <ButtonBase
        disableRipple
        aria-label={getReferenceBadgeAriaLabel(count)}
        data-testid={TeamChatReferenceBadgeTestIds.BADGE}
        sx={pillStyles}
        onClick={onClick}
      >
        <ChatIcon size={IconSize[12]} />
        <Typography component="span" sx={pillCountStyles} variant="regular12">
          {count}
        </Typography>
      </ButtonBase>
    </PopTipCDS>
  );
});
TeamChatReferenceBadge.propTypes = {
  entityType: PropTypes.oneOf(Object.values(ENTITY_REFERENCE_TYPE)).isRequired,
  entityId: PropTypes.string.isRequired,
  chatId: PropTypes.string,
};

export default TeamChatReferenceBadge;
